import { buildControllerRules } from "@gettactic/helpers/src/hook-form/buildControllerRules";
import { buildErrorMessageForName } from "@gettactic/helpers/src/hook-form/buildErrorMessageForName";
import { LooseRegisterOptions } from "@gettactic/helpers/src/hook-form/types";
import React from "react";
import { FieldValues } from "react-hook-form";
import { EmailInput } from "./EmailInput";
import { HookFormEmailInputProps } from "./EmailInput.types";

/**
 * Standard email validation pattern
 * Matches most valid email addresses according to RFC 5322
 */
const EMAIL_PATTERN =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/**
 * Email validation function
 */
export function validateEmail(email: string): boolean | string {
  if (!email || email.trim() === "") {
    return true; // Let required validation handle empty values
  }
  if (!EMAIL_PATTERN.test(email)) {
    return "Please enter a valid email address";
  }
  return true;
}

/**
 * `EmailInput` registered in a react-hook-form form, with email format validation ("Please enter
 * a valid email address"); empty values are left to `isRequired`.
 */
export const HookFormEmailInput = <T extends FieldValues>(
  props: HookFormEmailInputProps<T>
): React.ReactElement => {
  const { form, name, rules, ...rest } = props;

  // Build email validation rules
  // These will be merged with user-provided rules, with user rules taking precedence
  const emailValidationRules: LooseRegisterOptions = {
    pattern: {
      value: EMAIL_PATTERN,
      message: "Please enter a valid email address"
    },
    validate: {
      email: (value: unknown) => {
        const strValue = value as string;
        if (!strValue || strValue.trim() === "") {
          return true; // Let required validation handle empty values
        }
        return (
          EMAIL_PATTERN.test(strValue) || "Please enter a valid email address"
        );
      }
    }
  };

  return (
    <EmailInput
      {...form.register(
        name,
        buildControllerRules(
          {
            form,
            name,
            rules,
            isRequired: props.isRequired
          },
          emailValidationRules
        )
      )}
      error={buildErrorMessageForName(form, name)}
      {...rest}
    />
  );
};
